import moment from 'moment';

import * as Sentry from '@sentry/vue';
import { AxiosResponse } from 'axios';
import { Queue } from '../globals/queue';
import { Timespan } from './timespans';
import { BaseService } from './_base';

import { getLocaleDatetimeString } from '../globals/helpers';
import { v4 as uuidv4 } from 'uuid';
import { connection } from '../globals/internet';
import { session } from '../globals/session';
import { Ref, ref } from 'vue';
import { localStorageMutex, MUTEX_NOT_ACQUIRED } from '../globals/mutex';
import { TagAssignment } from './projects';


interface EmployeeTrack {
    id: string;
}


interface NewTimestamp {
    id: string;
    employee_id: string;
    time: string;
    stamp_type: string;

    description?: string;
    tag_assignments?: TagAssignment[];
    meta: any;
}

interface UpdateTimestamp {
    id: string;
    employee_id: string;
    description?: string;
    tag_assignments?: TagAssignment[];
}

interface OfflineAction {
    token: string;
    action: string;
    timestamp: NewTimestamp|UpdateTimestamp;
}

// How long an uploaded-but-unconfirmed stamp stays in the optimistic overlay
// before we stop waiting for the server's report to echo its local_id. A
// backstop only — confirmation normally lands on the next workday-report fetch,
// well within this window.
const PENDING_CONFIRMATION_TTL_MS = 30 * 1000;


class TrackingService extends BaseService<EmployeeTrack> {
    queue: Queue<OfflineAction>;
    isQueueProcessing: boolean;
    processedItems: Ref<number>;

    // Stamps uploaded to the server but not yet seen in a fetched workday
    // report. They stay in the optimistic overlay (alongside the unsent queue)
    // until a report confirms them by local_id — otherwise a synced stamp blinks
    // out in the gap between the queue pop and the (cache-first) report refresh.
    pendingConfirmation: Array<{ action: OfflineAction; since: number }>;

    constructor() {
        super("/api/v2/tracking/employees/");
        this.cacheTimeout = 5;
        this.queue = new Queue<OfflineAction>('tracking');
        this.isQueueProcessing = false;
        this.processedItems = ref(0);
        this.pendingConfirmation = [];

        // The overlay is per-user. Drop uploaded-but-unconfirmed stamps on
        // logout/account switch so a previous user's stamp can't surface in the
        // next user's report overlay (they're already on the server, so nothing
        // is lost). Mirrors pageStore's reset handling.
        session.on('reset', () => { this.pendingConfirmation = []; });
    }

    addTimestamp(employeeId: string, comment?: string, tagAssignments?: TagAssignment[], meta?: any, authToken?: string, stampType?: string) {
        return this._post(employeeId + '/add-timestamp/', {
            description: comment,
            tag_assignments: tagAssignments,
            meta: meta,
            stamp_type: stampType,
        }, authToken);
    }

    getQueuedActions() {
        return this.queue.getQueue();
    }

    // The optimistic overlay shown in the UI: stamps still awaiting upload
    // (queue) plus stamps uploaded but not yet confirmed in a server report
    // (pendingConfirmation). Pending come first so applyQueuedActions sees them
    // in chronological order — they were created before anything still queued.
    getUnconfirmedActions() {
        this.prunePendingConfirmation();
        return [...this.pendingConfirmation.map(p => p.action), ...this.queue.getQueue()];
    }

    // Once a fetched workday report echoes a pending stamp's local_id, the
    // server has it for real, so stop tracking it (applyQueuedActions dedupes it
    // by local_id from here on). 'update' actions carry no local_id, so they age
    // out via the TTL backstop instead.
    confirmSyncedActions(workdays: Array<{ timespans: Array<any> }>) {
        const syncedLocalIds = new Set<string>();
        for (const workday of workdays) {
            for (const timespan of workday.timespans) {
                if (timespan.checkin?.local_id) syncedLocalIds.add(timespan.checkin.local_id);
                if (timespan.checkout?.local_id) syncedLocalIds.add(timespan.checkout.local_id);
            }
        }
        this.pendingConfirmation = this.pendingConfirmation.filter(p => !syncedLocalIds.has(p.action.timestamp.id));
        this.prunePendingConfirmation();
    }

    // Backstop: drop anything we never saw confirmed within the TTL so a phantom
    // stamp can't linger in the overlay indefinitely. Only sweeps while online —
    // offline we can't fetch a report to confirm a genuinely-synced stamp, so it
    // must keep showing until we reconnect rather than blink out at the TTL.
    prunePendingConfirmation() {
        if (!connection.isConnected.value) return;
        const cutoff = Date.now() - PENDING_CONFIRMATION_TTL_MS;
        this.pendingConfirmation = this.pendingConfirmation.filter(p => p.since >= cutoff);
    }

    async addTimestampToQueue(employeeId: string, intent: string, comment?: string, tagAssignments?: TagAssignment[], meta?: any, time?: string) {
        // Ensure the last element in the queue differs from the new one
        const mostRecentlyAdded = this.queue.mostRecentlyAdded();
        if (mostRecentlyAdded && mostRecentlyAdded.timestamp.stamp_type === intent && mostRecentlyAdded.timestamp.employee_id === employeeId) {
            return mostRecentlyAdded;
        };

        const localId = "$local|" + uuidv4();
        meta = meta || {};
        meta.local_id = localId;
        const ts = {
            id: localId,
            employee_id: employeeId,
            time: time || getLocaleDatetimeString(),
            stamp_type: intent,
            description: comment,
            tag_assignments: tagAssignments,
            meta: meta,
        };
        const state = await localStorageMutex('services__tracking__processQueue', async () => {
            this.queue.push({
                token: localStorage.accessToken as string,
                action: 'add',
                timestamp: ts,
            });
        }, 10 * 1000);

        if (state === MUTEX_NOT_ACQUIRED) return null;

        return ts;
    }

    async addTimestampUpdateToQueue(employeeId: string, checkinTimestampId: string, description?: string, tagAssignments?: TagAssignment[]) {
        await localStorageMutex('services__tracking__processQueue', async () => {
            // Find timestamp in queue and update its values
            const queue = this.queue.getQueue();
            for (const action of queue) {
                if (action.timestamp.id !== checkinTimestampId) continue;
                if (description !== undefined) action.timestamp.description = description;
                if (tagAssignments !== undefined) action.timestamp.tag_assignments = tagAssignments;
                this.queue.commitQueue(queue);
                return;
            }

            // If timestamp is not in queue, add a change action
            this.queue.push({
                token: localStorage.accessToken as string,
                action: 'update',
                timestamp: {
                    id: checkinTimestampId,
                    employee_id: employeeId,
                    description: description,
                    tag_assignments: tagAssignments,
                }
            });
        }, 10 * 1000);
    }

    updateComment(employeeId: string, comment?: string, tagAssignments?: TagAssignment[]) {
        return this._post(employeeId + '/update-comment-and-tag-assignments/', {
            "description": comment,
            "tag_assignments": tagAssignments,
        });
    }

    updateTimespan(employeeId: string, timestampId: string, comment?: string, tagAssignments?: TagAssignment[]) {
        return this._post(employeeId + '/update-timespan/', {
            "timestamp_id": timestampId,
            "description": comment,
            "tag_assignments": tagAssignments,
        });
    }

    activeTimespan(employeeId: string): Promise<AxiosResponse<Timespan|string>> {
        return this._get(employeeId + '/active-timespan/') as unknown as Promise<AxiosResponse<Timespan|string>>;
    }

    async processQueue() {
        if (!connection.isConnected.value) return;

        // Only acquire a mutex if there's at least one item to process
        if (!this.queue.peek()) return;

        localStorageMutex('services__tracking__processQueue', () => this.drainQueue());
    }

    async drainQueue() {
        try {
            let processedItems = 0;
            while (this.queue.peek()) {
                const action = this.queue.peek();

                try {
                    if (action?.action === 'add') {
                        action.timestamp.meta.tracked_time = action.timestamp.time;
                        const start = Date.now();
                        await this.addTimestamp(action.timestamp.employee_id, action.timestamp.description, action.timestamp.tag_assignments, action.timestamp.meta, action.token, action.timestamp.stamp_type);
                        Sentry.addBreadcrumb({
                            category: 'tracking',
                            type: 'http',
                            level: 'info',
                            message: `add-timestamp POST succeeded (${action.timestamp.stamp_type})`,
                            data: {
                                duration_ms: Date.now() - start,
                                stamp_type: action.timestamp.stamp_type,
                                local_id: action.timestamp.id,
                                employee_id: action.timestamp.employee_id,
                                queue_remaining: this.queue.count() - 1,
                            },
                        });
                        this.queue.pop();
                        // Keep it in the optimistic overlay until a workday
                        // report confirms it — see pendingConfirmation.
                        this.pendingConfirmation.push({ action, since: Date.now() });
                        processedItems += 1;
                    } else if (action?.action === 'update') {
                        const start = Date.now();
                        await this.updateTimespan(action.timestamp.employee_id, action.timestamp.id, action.timestamp.description, action.timestamp.tag_assignments);
                        Sentry.addBreadcrumb({
                            category: 'tracking',
                            type: 'http',
                            level: 'info',
                            message: 'update-timespan POST succeeded',
                            data: {
                                duration_ms: Date.now() - start,
                                timestamp_id: action.timestamp.id,
                                employee_id: action.timestamp.employee_id,
                                queue_remaining: this.queue.count() - 1,
                            },
                        });
                        this.queue.pop();
                        // Keep it in the optimistic overlay until a workday
                        // report confirms it — see pendingConfirmation.
                        this.pendingConfirmation.push({ action, since: Date.now() });
                        processedItems += 1;
                    } else {
                        console.warn('Unknown action: ', action);
                        await new Promise(resolve => setTimeout(resolve, 10000));
                    }
                } catch (error) {
                    // A 403 means this action's token is expired/revoked (or the
                    // action is no longer permitted). It can never succeed on retry
                    // and would otherwise block the whole queue forever, so drop it
                    // and continue. Transient errors (network, 5xx) are re-thrown so
                    // the queue is left intact and retried on the next tick.
                    if ((error as any)?.response?.status !== 403) throw error;
                    Sentry.captureException(error, { tags: { source: 'TrackingService.processQueue', outcome: 'dropped_403' } });
                    this.queue.pop();
                }
            }
            if (processedItems > 0) {
                this.processedItems.value += processedItems;
            }

        } catch (error) {
            Sentry.captureException(error, { tags: { source: 'TrackingService.processQueue' } });
            console.log('Error: ', error);
        }
    }
}

const trackingService = new TrackingService();

export { trackingService, TrackingService };
